/**
 * The width scale shared by every text-like control (TextInput, NumberInput,
 * PhoneInput, PasswordInput, …). Callers pick a token rather than a pixel
 * value so fields line up across a form.
 *
 * `fit` sizes to the content; `full` fills the container — reach for `full`
 * when the surrounding card or grid cell should govern the width.
 */
export type InputWidth =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "fit"
  | "full";

/** Written out in full so Tailwind can see each class name. */
export const inputWidthClasses: Record<InputWidth, string> = {
  xs: "w-24",
  sm: "w-28",
  md: "w-48",
  lg: "w-64",
  xl: "w-96",
  // The widest fixed size: a multi-line description or notes field, where xl
  // is too narrow to show a sentence without wrapping every few words.
  "2xl": "w-[32rem]",
  fit: "w-fit",
  full: "w-full",
};

/**
 * The same scale in pixels, for the rare caller that needs the number rather
 * than the class — e.g. sizing a fixed-resolution image (a static map tile) to
 * match the field above it, so the two can't drift apart.
 *
 * `fit` and `full` size from context, so they have no fixed pixel value.
 */
export const inputWidthPx: Record<InputWidth, number | undefined> = {
  xs: 96,
  sm: 112,
  md: 192,
  lg: 256,
  xl: 384,
  "2xl": 512,
  fit: undefined,
  full: undefined,
};
